import type {
	AddressStats,
	CityContext,
	MacroregionContext,
	MappedLunrSearchResult,
	RegionContext,
	SearchAddressesResult
} from '~/types/address-context';
import type {
	Address,
	AddressData,
	Addresses
} from '~~/core/entities/Addresses';
import type { ComputedRef, Ref } from 'vue';

import { AddressUtilsMultiCity } from '~/utils/AddressUtils';

import { useAddressesMultiCityInternal } from './useAddressesMultiCity';

/**
 * Interface unificada para ambas as versões do useAddresses
 */
export interface UseAddressesReturn {
	addresses: Ref<Addresses[]>;
	state: ComputedRef<string>;
	macroregions: ComputedRef<Address[]>;
	cities: ComputedRef<Address[]>;
	regions: ComputedRef<AddressData[]>;
	filteredRegions: ComputedRef<MappedLunrSearchResult[]>;
	filteredCities: ComputedRef<Address[]>;

	// Métodos multi-cidade (disponíveis apenas na versão multi-cidade)
	getStateByCity?: (city: string) => string | undefined;
	getMacroregionByCity?: (state: string, city: string) => string | undefined;
	getRegionInfo?: (
		state: string,
		regionLabel: string
	) => AddressData | undefined;
	formatAddressDisplay?: (
		region?: string,
		city?: string,
		state?: string,
		macroregion?: string
	) => string;
	isValidAddress?: (
		state?: string,
		city?: string,
		region?: string,
		macroregion?: string
	) => boolean;
	getRegionContext?: (
		state: string,
		regionLabel: string
	) => RegionContext | null;
	getCityContext?: (state: string, city: string) => CityContext | null;
	getMacroregionContext?: (
		state: string,
		macroregion: string
	) => MacroregionContext | null;
	getAddressStats?: () => AddressStats;
	searchAddresses?: (searchTerm: string) => SearchAddressesResult;
	getAllStates?: () => string[];
	getCitiesByState?: (state: string) => Address[];
	getMacroregionsByState?: (state: string) => Address[];
	getRegionsByState?: (state: string) => AddressData[];
	getRegionsByCity?: (state: string, city: string) => AddressData[];
	getRegionsByMacroregion?: (
		state: string,
		macroregion: string
	) => AddressData[];
}

/**
 * Wrapper que retorna a versão multi-cidade
 */
export function useAddresses(searchTerm?: Ref<string>): UseAddressesReturn {
	return useAddressesMultiCityInternal(searchTerm);
}

/**
 * Objeto estático com métodos habilitados do AddressUtilsMultiCity
 * Criado uma única vez para evitar alocações repetidas
 */
const ENABLED_MULTI_CITY_METHODS = {
	getStateByCity: AddressUtilsMultiCity.getStateByCity,
	getMacroregionByCity: AddressUtilsMultiCity.getMacroregionByCity,
	getRegionInfo: AddressUtilsMultiCity.getRegionInfo,
	formatAddressDisplay: AddressUtilsMultiCity.formatAddressDisplay,
	isValidAddress: AddressUtilsMultiCity.isValidAddress,
	getRegionContext: AddressUtilsMultiCity.getRegionContext,
	getCityContext: AddressUtilsMultiCity.getCityContext,
	getMacroregionContext: AddressUtilsMultiCity.getMacroregionContext,
	getAddressStats: AddressUtilsMultiCity.getAddressStats,
	searchAddresses: AddressUtilsMultiCity.searchAddresses,
	getAllStates: AddressUtilsMultiCity.getAllStates,
	getCitiesByState: AddressUtilsMultiCity.getCitiesByState,
	getMacroregionsByState: AddressUtilsMultiCity.getMacroregionsByState,
	getRegionsByState: AddressUtilsMultiCity.getRegionsByState,
	getRegionsByCity: AddressUtilsMultiCity.getRegionsByCity,
	getRegionsByMacroregion: AddressUtilsMultiCity.getRegionsByMacroregion
} as const;

/**
 * Hook específico para acessar apenas métodos multi-cidade
 *
 * IMPORTANTE: Este hook NÃO cria watchers ou effects, apenas retorna
 * referências a funções estáticas do AddressUtilsMultiCity.
 * Isso evita vazamentos de memória por criação repetida de watchers.
 */
export function useAddressesMultiCityMethods() {
	// Retorna objeto estático pré-criado com referências aos métodos
	// Não cria watchers, effects ou qualquer estado reativo
	return ENABLED_MULTI_CITY_METHODS;
}
